import { always } from 'ramda';
import React, { FunctionComponent, useRef, useState } from 'react';
import { useOnClickOutside } from '../../../../common/helpers/customHooks';
import { IBindingCallback1, IBindingCallback2 } from '../../../../common/models/callback';
import EmailTextArea from '../../../../components/EmailTextArea';
import Icon from '../../../../components/Icon';
import RegularButton from '../../../../components/RegularButton';
import styles from './styles.module.scss';

interface IProps {
  messageTemplate: string;
  defaultMessageTemlpate: string;
  subjectTemplate: string;
  defaultSubjectTemplate: string;
  label: string | JSX.Element;
  messageTemplateHeader: string | JSX.Element | (JSX.Element | string)[];
  messageTemplateFooter: string | JSX.Element;
  subjectDisabled?: boolean;
  onSave: IBindingCallback2<string, string>;
  onPreview: IBindingCallback1<string>;
}

const MessageTemplateEditor: FunctionComponent<IProps> = ({
  messageTemplate,
  defaultMessageTemlpate,
  subjectTemplate,
  defaultSubjectTemplate,
  label,
  messageTemplateHeader,
  messageTemplateFooter,
  subjectDisabled = false,
  onPreview,
  onSave
}) => {
  const [message, setMessage] = useState(messageTemplate);
  const [subject, setSubject] = useState(subjectTemplate);
  const [isEditing, setIsEditing] = useState(false);
  const messageTemplateRef = useRef(null);

  const onCancel = () => {
    setIsEditing(false);
    setMessage(messageTemplate);
  };

  const onReset = () => { setMessage(defaultMessageTemlpate); setSubject(defaultSubjectTemplate); };

  const onSaveChanges = () => {
    setIsEditing(false);
    onSave(message, subject);
  };

  useOnClickOutside(messageTemplateRef, isEditing ? onSaveChanges : always(undefined));

  return (
    <div className={styles.messageTemplateEditor} ref={messageTemplateRef}>
      <header className={`${styles.header} d-flex align-items-center justify-content-between`}>
        <div>
          <span className="settingsTitle">{label}</span>
        </div>
        <div>
          <div className={styles.buttonBlock}>
            <RegularButton
              onClick={() => onPreview(message)}
              textIntlId="preview"
              defaultMessage="Preview"
              icon={<Icon icon="preview" />}
              variant="tertiary"
            />
            {isEditing
              ? (
                <>
                  <RegularButton
                    textIntlId="cancel"
                    defaultMessage="Cancel"
                    variant="tertiary"
                    onClick={onCancel}
                  />
                  <RegularButton
                    textIntlId="messageTemplateSettings.reset"
                    defaultMessage="Reset to default"
                    variant="secondary"
                    onClick={onReset}
                  />
                  <RegularButton
                    textIntlId="saveChanges"
                    defaultMessage="Save changes"
                    variant="primary"
                    icon={<Icon icon="check" />}
                    onClick={onSaveChanges}
                  />
                </>
              )
              : (
                <RegularButton
                  textIntlId="messageTemplateSettings.edit"
                  defaultMessage="Edit"
                  variant="secondary"
                  onClick={() => setIsEditing(true)}
                />
              )}
          </div>
        </div>
      </header>
      <main className={styles.main} onClick={() => setIsEditing(true)} role="presentation">
        <EmailTextArea
          variant="withSubject"
          subject={subject}
          onSubjectChange={setSubject}
          disabled={!isEditing}
          message={message}
          onChange={setMessage}
          customEmailHeader={messageTemplateHeader}
          customEmailFooter={messageTemplateFooter}
          subjectDisabled={subjectDisabled}
        />
      </main>
    </div>
  );
};

export default MessageTemplateEditor;
